import axios from 'axios';
import { refreshToken as doRefresh } from './auth.js';

const BASE = import.meta.env.VITE_API_URL ?? '/api/v1';

const client = axios.create({ baseURL: BASE, timeout: 15_000 });

function loadTokens() {
  try {
    const raw = localStorage.getItem('iex_tokens');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null; // localStorage inacessível (SSR ou private mode)
  }
}

// Injeta Bearer token em todas as requisições
client.interceptors.request.use((config) => {
  const tokens = loadTokens();
  if (tokens?.access) {
    config.headers.Authorization = `Bearer ${tokens.access}`;
  }
  return config;
});

// Um 401 aqui hoje em dia só significa sessão inválida/expirada de verdade
// (token corrompido, sessão revogada, timeout de ociosidade) — o caso de
// "identidade válida mas cadastro de paciente incompleto" virou 403 no
// backend (ver api/app/core/rbac.py::require_patient) exatamente para não
// cair neste caminho. Antes de desistir, tenta renovar a sessão uma vez via
// refresh token; só limpa tudo e avisa a UI se a renovação também falhar.
// `refreshPromise` compartilhada evita disparar N refreshes em paralelo
// quando várias chamadas 401am ao mesmo tempo.
let refreshPromise = null;

async function refreshSession(refreshToken) {
  const data = await doRefresh(refreshToken);
  const tokens = { access: data.access_token, refresh: data.refresh_token };
  localStorage.setItem('iex_tokens', JSON.stringify(tokens));
  return tokens;
}

client.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    const tokens = loadTokens();

    if (err.response?.status === 401 && tokens?.refresh && !original?._retriedAfterRefresh) {
      original._retriedAfterRefresh = true;
      try {
        refreshPromise ??= refreshSession(tokens.refresh).finally(() => { refreshPromise = null; });
        const fresh = await refreshPromise;
        original.headers.Authorization = `Bearer ${fresh.access}`;
        return client(original);
      } catch {
        // refresh também falhou — sessão realmente morreu, cai no logout abaixo.
      }
    }

    if (err.response?.status === 401) {
      localStorage.removeItem('iex_tokens');
      window.dispatchEvent(new Event('iex:unauthorized'));
    }
    return Promise.reject(err);
  }
);

export default client;
